import { cn } from "@universe/ui/lib/utils";
import { Repeat } from "lucide-react";
import { useTranslations } from "next-intl";

import CurrencyFlag from "@/components/currency-flag";

interface LedgerEntryListBasicInfoCombinedProps {
  entry: CombinedFxEntry | null;
  isHeader?: boolean;
  className?: string;
}

export default function LedgerEntryListBasicInfoCombined({
  entry,
  isHeader = false,
  className,
}: LedgerEntryListBasicInfoCombinedProps) {
  const tLedgerTransactions = useTranslations("ledger.transaction");

  // Defensive checks for missing entries (e.g., during pagination)
  if (!entry || !entry.creditEntry || !entry.debitEntry) return null;

  const { creditEntry, debitEntry } = entry;

  // "From" is the debit (negative/spent), "To" is the credit (positive/received)
  const fromCurrency = debitEntry.product;
  const toCurrency = creditEntry.product;

  const title = tLedgerTransactions("exchange", {
    from: fromCurrency,
    to: toCurrency,
  });

  // Consistent flag size - slightly larger for header
  const flagSize = isHeader ? "26px" : "24px";

  return (
    <div className={cn("flex items-center gap-1.5 overflow-hidden", className)}>
      {/* Exchange icon */}
      <Repeat
        strokeWidth={2}
        className={cn(
          "text-primary w-7 h-7 -translate-x-0.5 scale-90",
          isHeader && "lg:h-7 lg:w-7"
        )}
      />

      {/* Paired currency flags - stacked with "to" overlapping bottom-right */}
      <div
        className="relative mr-1.5"
        style={{
          width: isHeader ? "38px" : "36px",
          height: isHeader ? "38px" : "36px",
        }}
      >
        <div className="absolute top-0 left-0">
          <CurrencyFlag size={flagSize} currency={fromCurrency} />
        </div>
        <div className="absolute bottom-0 right-0">
          <CurrencyFlag size={flagSize} currency={toCurrency} />
        </div>
      </div>

      {/* Title */}
      <div className="flex items-center">
        <p
          className={cn(
            "font-medium text-base md:text-lg",
            isHeader && "max-w-[calc(100vw-150px)] sm:w-[330px] lg:w-[350px]"
          )}
        >
          {title}
        </p>
      </div>
    </div>
  );
}
